/*
 * Copyright (c) 2023-2024 - Restate Software, Inc., Restate GmbH
 *
 * This file is part of the Restate SDK for Node.js/TypeScript,
 * which is released under the MIT license.
 *
 * You can find a copy of the license in file LICENSE in the root
 * directory of this repository or package, or at
 * https://github.com/restatedev/sdk-typescript/blob/main/LICENSE
 */

import {
  type Service,
  type ServiceDefinitionFrom,
  type VirtualObject,
  type WorkflowDefinitionFrom,
  type Workflow,
  type VirtualObjectDefinitionFrom,
  type Serde,
  serde,
  type JournalValueCodec,
} from "@restatedev/restate-sdk-core";
import {
  ConnectionOpts,
  Ingress,
  IngressClient,
  IngressSendClient,
  IngressWorkflowClient,
  Output,
  Send,
  ScopedIngress,
  WorkflowSubmission,
} from "./api.js";

import { Opts, SendOpts } from "./api.js";
import {
  abortableSleep,
  backoffDelay,
  defaultShouldRetry,
  parseRetryAfter,
  resolveRetryPolicy,
} from "./retry.js";

/**
 * Connect to the restate Ingress
 *
 * @param opts connection options
 * @returns a connection the the restate ingress
 */
export function connect(opts: ConnectionOpts): Ingress {
  return new HttpIngress(opts);
}

export class HttpCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly responseText: string,
    public override readonly message: string
  ) {
    super(message);
  }
}

type InvocationParameters<I> = {
  component: string;
  handler: string;
  key?: string;
  send?: boolean;
  opts?: Opts<I, unknown> | SendOpts<I>;
  parameter?: I;
  method?: string;
  scope?: string;
};

function optsFromArgs(args: unknown[]): {
  parameter?: unknown;
  opts?: Opts<unknown, unknown> | SendOpts<unknown>;
} {
  let parameter: unknown;
  let opts: Opts<unknown, unknown> | SendOpts<unknown> | undefined;
  switch (args.length) {
    case 0: {
      break;
    }
    case 1: {
      if (args[0] instanceof Opts) {
        opts = args[0];
      } else if (args[0] instanceof SendOpts) {
        opts = args[0];
      } else {
        parameter = args[0];
      }
      break;
    }
    case 2: {
      parameter = args[0];
      if (args[1] instanceof Opts) {
        opts = args[1];
      } else if (args[1] instanceof SendOpts) {
        opts = args[1];
      } else {
        throw new TypeError(
          "The second argument must be either Opts or SendOpts"
        );
      }
      break;
    }
    default: {
      throw new TypeError("unexpected number of arguments");
    }
  }
  return {
    parameter,
    opts,
  };
}

const IDEMPOTENCY_KEY_HEADER = "idempotency-key";
const LIMIT_KEY_HEADER = "x-restate-limit-key";

const doComponentInvocation = async <I, O>(
  opts: ConnectionOpts,
  params: InvocationParameters<I>
): Promise<O> => {
  let attachable = false;
  //
  // ingress URL
  //
  let url: string;
  if (params.scope) {
    // Scoped path: /restate/scope/{scope}/{call|send}/{service}/{key?}/{handler}
    const pathType = params.send ? "send" : "call";
    const parts = [
      opts.url,
      "restate/scope",
      encodeURIComponent(params.scope),
      pathType,
      params.component,
    ];
    if (params.key) {
      parts.push(encodeURIComponent(params.key));
    }
    parts.push(params.handler);
    url = parts.join("/");
    if (params.send && params.opts instanceof SendOpts) {
      const delay = params.opts.delay();
      if (delay) url += `?delay=${delay}ms`;
    }
  } else {
    const fragments = [opts.url, params.component];
    if (params.key) {
      fragments.push(encodeURIComponent(params.key));
    }
    fragments.push(params.handler);
    if (params.send ?? false) {
      if (params.opts instanceof SendOpts) {
        fragments.push(computeDelayAsIso(params.opts));
      } else {
        fragments.push("send");
      }
    }
    url = fragments.join("/");
  }
  //
  // request body
  //
  const inputSerde = params.opts?.opts.input ?? opts.serde ?? serde.json;

  const { body, contentType } = serializeBodyWithContentType(
    params.parameter,
    inputSerde,
    opts.journalValueCodec
  );
  //
  // headers
  //
  const headers = {
    ...(opts.headers ?? {}),
    ...(params.opts?.opts?.headers ?? {}),
  };
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  //
  // idempotency
  //
  const idempotencyKey = params.opts?.opts.idempotencyKey;
  if (idempotencyKey) {
    headers[IDEMPOTENCY_KEY_HEADER] = idempotencyKey;
    attachable = true;
  }
  //
  // limit key
  //
  const limitKey = params.opts?.opts.limitKey;
  if (limitKey) {
    headers[LIMIT_KEY_HEADER] = limitKey;
  }

  // Abort signal, if any
  const userSignal = params.opts?.opts.signal;
  const timeout = params.opts?.opts.timeout;
  if (userSignal !== undefined && timeout !== undefined) {
    throw new Error(
      "You can't specify both signal and timeout options at the same time"
    );
  }
  // A fresh timeout signal is minted per attempt below — a single
  // AbortSignal.timeout() would already be aborted on the second attempt.
  const attemptSignal = (): AbortSignal | undefined =>
    userSignal ??
    (timeout !== undefined ? AbortSignal.timeout(timeout) : undefined);

  //
  // retries
  //
  // Retries are opt-in (opts.retry) AND only ever attempted when the call
  // carries an idempotency key: Restate then dedupes the request, so re-issuing
  // it after an ambiguous failure (network error, 429, 5xx) safely attaches to
  // the in-flight/completed invocation instead of starting a duplicate. Without
  // a key, retrying is unsafe.
  //
  const retryPolicy = idempotencyKey
    ? resolveRetryPolicy(opts.retry)
    : undefined;
  const shouldRetry = retryPolicy?.shouldRetry ?? defaultShouldRetry;

  //
  // make the call
  //
  let httpResponse: Response;
  for (let attempt = 0; ; attempt++) {
    try {
      httpResponse = await fetch(url, {
        method: params.method ?? "POST",
        headers,
        body,
        signal: attemptSignal(),
      });
    } catch (e) {
      // network error (fetch rejected) — ambiguous
      if (
        retryPolicy &&
        attempt < retryPolicy.maxAttempts - 1 &&
        !userSignal?.aborted &&
        shouldRetry({ kind: "network", error: e }, attempt)
      ) {
        await abortableSleep(backoffDelay(retryPolicy, attempt), userSignal);
        continue;
      }
      throw e;
    }
    if (httpResponse.ok) {
      break;
    }
    // Read the error body once; it is reused for the retry decision and, if we
    // give up, for the thrown HttpCallError.
    const errorBody = await httpResponse.text();
    if (
      retryPolicy &&
      attempt < retryPolicy.maxAttempts - 1 &&
      !userSignal?.aborted &&
      shouldRetry(
        {
          kind: "response",
          status: httpResponse.status,
          headers: httpResponse.headers,
          body: errorBody || undefined,
        },
        attempt
      )
    ) {
      const retryAfter = parseRetryAfter(httpResponse.headers);
      await abortableSleep(
        backoffDelay(retryPolicy, attempt, retryAfter),
        userSignal
      );
      continue;
    }
    throw new HttpCallError(
      httpResponse.status,
      errorBody,
      `Request failed: ${httpResponse.status}\n${errorBody}`
    );
  }
  const responseBuf = new Uint8Array(await httpResponse.arrayBuffer());
  if (!params.send) {
    const decodedBuf = opts.journalValueCodec
      ? await opts.journalValueCodec.decode(responseBuf)
      : responseBuf;
    const outputSerde = params.opts?.opts.output ?? opts.serde ?? serde.json;
    return outputSerde.deserialize(decodedBuf) as O;
  }
  const json = serde.json.deserialize(responseBuf) as O;
  return { ...json, attachable };
};

const doWorkflowHandleCall = async <O>(
  opts: ConnectionOpts,
  wfName: string,
  wfKey: string,
  op: "output" | "attach",
  callOpts?: Opts<unknown, O> | SendOpts<unknown>
): Promise<O> => {
  const outputSerde = callOpts?.opts.output ?? opts.serde ?? serde.json;
  //
  // headers
  //
  const headers = {
    ...(opts.headers ?? {}),
  };
  //
  // make the call
  //
  const url = `${opts.url}/restate/workflow/${wfName}/${encodeURIComponent(
    wfKey
  )}/${op}`;

  // Abort signal, if any
  let signal: AbortSignal | undefined;
  if (
    callOpts?.opts?.signal !== undefined &&
    callOpts?.opts?.timeout !== undefined
  ) {
    throw new Error(
      "You can't specify both signal and timeout options at the same time"
    );
  } else if (callOpts?.opts?.signal !== undefined) {
    signal = callOpts?.opts?.signal;
  } else if (callOpts?.opts?.timeout !== undefined) {
    signal = AbortSignal.timeout(callOpts?.opts?.timeout);
  }

  const httpResponse = await fetch(url, {
    method: "GET",
    headers,
    signal,
  });
  if (httpResponse.ok) {
    const responseBuf = new Uint8Array(await httpResponse.arrayBuffer());
    const decodedBuf = opts.journalValueCodec
      ? await opts.journalValueCodec.decode(responseBuf)
      : responseBuf;
    return outputSerde.deserialize(decodedBuf) as O;
  }
  const body = await httpResponse.text();
  throw new HttpCallError(
    httpResponse.status,
    body,
    `Request failed: ${httpResponse.status}\n${body}`
  );
};

class HttpIngress implements Ingress {
  constructor(private readonly opts: ConnectionOpts) {}

  private proxy(component: string, key?: string, send?: boolean) {
    return new Proxy(
      {},
      {
        get: (_target, prop) => {
          const handler = prop as string;
          return (...args: unknown[]) => {
            const { parameter, opts } = optsFromArgs(args);
            return doComponentInvocation<unknown, unknown>(this.opts, {
              component,
              handler,
              key,
              parameter,
              opts,
              send,
            });
          };
        },
      }
    );
  }

  serviceClient<D>(opts: ServiceDefinitionFrom<D>): IngressClient<Service<D>> {
    return this.proxy(opts.name) as IngressClient<Service<D>>;
  }

  objectClient<D>(
    opts: VirtualObjectDefinitionFrom<D>,
    key: string
  ): IngressClient<VirtualObject<D>> {
    return this.proxy(opts.name, key) as IngressClient<VirtualObject<D>>;
  }

  workflowClient<D>(
    opts: WorkflowDefinitionFrom<D>,
    key: string
  ): IngressWorkflowClient<Workflow<D>> {
    const component = opts.name;
    const conn = this.opts;

    const workflowSubmit = async (
      ...args: unknown[]
    ): Promise<WorkflowSubmission<unknown>> => {
      const { parameter, opts } = optsFromArgs(args);
      const res: Send = await doComponentInvocation(conn, {
        component,
        handler: "run",
        key,
        send: true,
        parameter,
        opts,
      });

      return {
        invocationId: res.invocationId,
        status: res.status,
        attachable: true,
      };
    };

    const workflowAttach = (opts?: Opts<void, unknown>) =>
      doWorkflowHandleCall(conn, component, key, "attach", opts);

    const workflowOutput = async (
      opts?: Opts<void, unknown>
    ): Promise<Output<unknown>> => {
      try {
        const result = await doWorkflowHandleCall(
          conn,
          component,
          key,
          "output",
          opts
        );

        return {
          ready: true,
          result,
        };
      } catch (e) {
        if (!(e instanceof HttpCallError) || e.status !== 470) {
          throw e;
        }
        return {
          ready: false,
          get result() {
            throw new Error("Calling result() on a non ready workflow");
          },
        };
      }
    };

    return new Proxy(
      {},
      {
        get: (_target, prop) => {
          const handler = prop as string;
          if (handler === "workflowSubmit") {
            return workflowSubmit;
          } else if (handler === "workflowAttach") {
            return workflowAttach;
          } else if (handler === "workflowOutput") {
            return workflowOutput;
          }
          // shared handlers pass trough via the ingress's normal invocation form
          // i.e. POST /<svc>/<key>/<handler>
          return (...args: unknown[]) => {
            const { parameter, opts } = optsFromArgs(args);
            return doComponentInvocation(conn, {
              component,
              handler,
              key,
              parameter,
              opts,
            });
          };
        },
      }
    ) as IngressWorkflowClient<Workflow<D>>;
  }

  objectSendClient<D>(
    opts: VirtualObjectDefinitionFrom<D>,
    key: string
  ): IngressSendClient<VirtualObject<D>> {
    return this.proxy(opts.name, key, true) as IngressSendClient<
      VirtualObject<D>
    >;
  }

  serviceSendClient<D>(
    opts: ServiceDefinitionFrom<D>
  ): IngressSendClient<Service<D>> {
    return this.proxy(opts.name, undefined, true) as IngressSendClient<
      Service<D>
    >;
  }

  scope(scopeKey: string): ScopedIngress {
    const conn = this.opts;
    const scopedProxy = (component: string, key?: string, send?: boolean) =>
      new Proxy(
        {},
        {
          get: (_target, prop) => {
            const handler = prop as string;
            return (...args: unknown[]) => {
              const { parameter, opts } = optsFromArgs(args);
              return doComponentInvocation<unknown, unknown>(conn, {
                component,
                handler,
                key,
                parameter,
                opts,
                send,
                scope: scopeKey,
              });
            };
          },
        }
      );

    return {
      serviceClient: <D>(opts: ServiceDefinitionFrom<D>) =>
        scopedProxy(opts.name) as IngressClient<Service<D>>,
      serviceSendClient: <D>(opts: ServiceDefinitionFrom<D>) =>
        scopedProxy(opts.name, undefined, true) as IngressSendClient<
          Service<D>
        >,
      objectClient: <D>(opts: VirtualObjectDefinitionFrom<D>, key: string) =>
        scopedProxy(opts.name, key) as IngressClient<VirtualObject<D>>,
      objectSendClient: <D>(
        opts: VirtualObjectDefinitionFrom<D>,
        key: string
      ) =>
        scopedProxy(opts.name, key, true) as IngressSendClient<
          VirtualObject<D>
        >,
      workflowClient: <D>(
        opts: WorkflowDefinitionFrom<D>,
        key: string
      ): IngressWorkflowClient<Workflow<D>> => {
        const component = opts.name;

        const workflowSubmit = async (
          ...args: unknown[]
        ): Promise<WorkflowSubmission<unknown>> => {
          const { parameter, opts } = optsFromArgs(args);
          const res: Send = await doComponentInvocation(conn, {
            component,
            handler: "run",
            key,
            send: true,
            parameter,
            opts,
            scope: scopeKey,
          });
          return {
            invocationId: res.invocationId,
            status: res.status,
            attachable: true,
          };
        };

        const workflowAttach = (opts?: Opts<void, unknown>) =>
          doWorkflowHandleCall(conn, component, key, "attach", opts);

        const workflowOutput = async (
          opts?: Opts<void, unknown>
        ): Promise<Output<unknown>> => {
          try {
            const result = await doWorkflowHandleCall(
              conn,
              component,
              key,
              "output",
              opts
            );
            return { ready: true, result };
          } catch (e) {
            if (!(e instanceof HttpCallError) || e.status !== 470) {
              throw e;
            }
            return {
              ready: false,
              get result() {
                throw new Error("Calling result() on a non ready workflow");
              },
            };
          }
        };

        return new Proxy(
          {},
          {
            get: (_target, prop) => {
              const handler = prop as string;
              if (handler === "workflowSubmit") {
                return workflowSubmit;
              } else if (handler === "workflowAttach") {
                return workflowAttach;
              } else if (handler === "workflowOutput") {
                return workflowOutput;
              }
              return (...args: unknown[]) => {
                const { parameter, opts } = optsFromArgs(args);
                return doComponentInvocation(conn, {
                  component,
                  handler,
                  key,
                  parameter,
                  opts,
                  scope: scopeKey,
                });
              };
            },
          }
        ) as IngressWorkflowClient<Workflow<D>>;
      },
    };
  }

  async call<I, O>(opts: {
    service: string;
    handler: string;
    parameter: I;
    key?: string;
    scope?: string;
    opts?: Opts<I, O>;
  }): Promise<O> {
    return doComponentInvocation<I, O>(this.opts, {
      component: opts.service,
      handler: opts.handler,
      key: opts.key,
      scope: opts.scope,
      parameter: opts.parameter,
      send: false,
      opts: opts.opts,
    });
  }

  async send<I>(opts: {
    service: string;
    handler: string;
    parameter: I;
    key?: string;
    scope?: string;
    opts?: SendOpts<I>;
  }): Promise<Send> {
    return doComponentInvocation<I, Send>(this.opts, {
      component: opts.service,
      handler: opts.handler,
      key: opts.key,
      scope: opts.scope,
      parameter: opts.parameter,
      send: true,
      opts: opts.opts,
    });
  }

  async resolveAwakeable<T>(
    id: string,
    payload?: T,
    payloadSerde?: Serde<T>
  ): Promise<void> {
    const url = `${this.opts.url}/restate/a/${id}/resolve`;
    const { body, contentType } = serializeBodyWithContentType(
      payload,
      payloadSerde ?? this.opts.serde ?? serde.json,
      this.opts.journalValueCodec
    );
    const headers = {
      ...(this.opts.headers ?? {}),
    };
    if (contentType) {
      headers["Content-Type"] = contentType;
    }
    const httpResponse = await fetch(url, {
      method: "POST",
      headers,
      body,
    });
    if (!httpResponse.ok) {
      const body = await httpResponse.text();
      throw new HttpCallError(
        httpResponse.status,
        body,
        `Request failed: ${httpResponse.status}\n${body}`
      );
    }
  }

  async rejectAwakeable(id: string, reason: string): Promise<void> {
    const url = `${this.opts.url}/restate/a/${id}/reject`;
    const headers = {
      "Content-Type": "text/plain",
      ...(this.opts.headers ?? {}),
    };
    const httpResponse = await fetch(url, {
      method: "POST",
      headers,
      body: reason,
    });
    if (!httpResponse.ok) {
      const body = await httpResponse.text();
      throw new HttpCallError(
        httpResponse.status,
        body,
        `Request failed: ${httpResponse.status}\n${body}`
      );
    }
  }

  async result<T>(
    send: Send<T> | WorkflowSubmission<T>,
    resultSerde?: Serde<T>
  ): Promise<T> {
    if (!send.attachable) {
      throw new Error(
        `Unable to fetch the result for ${send.invocationId}.
        A service's result is stored only with an idempotencyKey is supplied when invocating the service.`
      );
    }
    //
    // headers
    //
    const headers = {
      ...(this.opts.headers ?? {}),
    };
    //
    // make the call
    const url = `${this.opts.url}/restate/invocation/${send.invocationId}/attach`;

    const httpResponse = await fetch(url, {
      method: "GET",
      headers,
    });
    if (httpResponse.ok) {
      const responseBuf = new Uint8Array(await httpResponse.arrayBuffer());
      const decodedBuf = this.opts.journalValueCodec
        ? await this.opts.journalValueCodec.decode(responseBuf)
        : responseBuf;
      return (resultSerde ?? this.opts.serde ?? serde.json).deserialize(
        decodedBuf
      ) as T;
    }
    const body = await httpResponse.text();
    throw new HttpCallError(
      httpResponse.status,
      body,
      `Request failed: ${httpResponse.status}\n${body}`
    );
  }
}

function computeDelayAsIso(opts: SendOpts): string {
  const delay = opts.delay();
  if (!delay) {
    return "send";
  }
  return `send?delay=${delay}ms`;
}

function serializeBodyWithContentType(
  body: unknown,
  serde: Serde<unknown>,
  journalValueCodec?: JournalValueCodec
): {
  body?: Uint8Array;
  contentType?: string;
} {
  let buffer = serde.serialize(body);
  if (journalValueCodec) {
    buffer = journalValueCodec.encode(buffer);
  }
  return {
    body: buffer,
    contentType: serde.contentType,
  };
}
